// Preact app entry — mount point. Per spec/tech-arch.md §3.1 + §4 and
// the P-022 build-plan row.
//
// **Boot sequence (D-083 contract + P-045 / D-140 AuthGate wrap).**
//   1. `loadPersistedState()` reads localStorage, validates against
//      `AppStateSchema`, runs migrations, and surfaces any failure via
//      the persistence-error stream as a side effect (consumed by
//      `<PersistenceBanner />` inside `<App />`).
//   2. If a persisted state was returned, seed the global store so the
//      first render reads persisted data — not `initialAppState`. This
//      avoids a flash of empty UI.
//   3. `attachPersistence(store)` wires the debounced (500 ms) write-
//      through subscriber. Called before `render()` so any setState
//      fired from the first React effect commits to localStorage.
//   4. Mount `<AuthGate><App /></AuthGate>` at `<div id="app">`. The
//      gate runs `ensureAnonAuth()` + `getCurrentSession()` on mount;
//      while pending it renders `<LoadingScreen />`, on no-session it
//      renders `<LoginScreen />`, and on success it renders `<App />`.
//      State seeding stays pre-render — chapter data is shared per
//      `prd-v2-auth.md` (no per-user ownership), so loading it before
//      the auth check is correct.
//
// **Coverage exclusion.** Excluded via vitest.config.ts (D-083) — the
// module top-level executes against a real DOM `#app` element at
// import time, which is hostile to unit tests. Verified via
// `npm run dev` smoke check; a Playwright app-level spec lands later
// when the app shell has interactive behavior worth pinning (P-024+).

import { render } from 'preact'

import { attachPersistence, loadPersistedState } from '../store/persistence'
import { store } from '../store/store'
import { App } from './app'
import { AuthGate } from './auth-gate'
import { rewriteOAuthCallbackUrlIfPresent } from './cc-callback-redirect-shim'
import '../styles/app.css'

// P-047 remediation (2026-05-27) — if the page loaded at the path-form
// CC OAuth callback URL (`${BASE_URL}/auth/cc-callback?code=...&state=...`),
// rewrite the URL into the hash form BEFORE any component reads
// `window.location.hash`. The hash router (D-085) then routes to
// `<CCCallbackScreen />` exactly as if the user navigated to the hash
// form directly. RFC 6749 §3.1.2 forbids fragments in OAuth redirect
// URIs (CC enforces), so this path-to-hash bridge is mandatory.
rewriteOAuthCallbackUrlIfPresent()

const { state } = loadPersistedState()
if (state !== null) store.setState(() => state)
attachPersistence(store)

const mount = document.getElementById('app')
if (!mount) {
  throw new Error('Mount point #app not found in index.html')
}
render(
  <AuthGate>
    <App />
  </AuthGate>,
  mount,
)
